import React, { useState, useEffect } from 'react';
import { 
  Send, Loader2, CheckCircle, AlertCircle, 
  ArrowLeft, Mail, Phone, MapPin, MessageCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { supabase } from '../lib/supabaseClient';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebaseClient';
import { INQUIRY_CATEGORIES } from '../constants';

export const Inquiries: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    category: INQUIRY_CATEGORIES[0],
    subject: '',
    message: ''
  });

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;

        if (currentSession) {
          setSession(currentSession);
          // Pre-fill if logged in
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', currentSession.user.id)
            .single();
          
          if (profileError) {
            console.warn("Profile fetch error:", profileError.message);
          } else if (profile) {
            setFormData(prev => ({
              ...prev,
              name: profile.name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
              email: profile.email || '',
              phone: profile.mobile || ''
            }));
          }
        }
      } catch (err: any) {
        console.error("Supabase init error:", err.message);
        // We don't set a global error here to avoid blocking the whole form for non-logged in users
      }
    };
    init();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // 1. Save to Firestore in the background
      let dbSaved = false;
      try {
        await addDoc(collection(db, 'inquiries'), {
          user_id: session?.user?.id || null,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          location: formData.location,
          category: formData.category,
          subject: formData.subject,
          message: formData.message,
          status: 'Open',
          createdAt: serverTimestamp()
        });
        dbSaved = true;
      } catch (dbErr) {
        console.warn("Failed to save to Firestore:", dbErr);
      }

      // 2. Proxy email through our server to bypass client-side blocks
      let emailSent = false;
      try {
        const response = await fetch("/api/inquiry", {
          method: "POST",
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            location: formData.location,
            category: formData.category,
            subject: formData.subject,
            message: formData.message,
            _cc: "info@villaavoils.com"
          })
        });

        if (response.ok) {
          const data = await response.json();
          // We moved activation check to server, but double check here or trust server 403
          emailSent = true;
        } else {
          const data = await response.json();
          console.warn("Server inquiry error:", data);
          if (data.activationRequired || (data.message && data.message.toLowerCase().includes("activate"))) {
             throw new Error("ACTION REQUIRED: FormSubmit needs to be activated for brianmugambikaranja@gmail.com. Please check your email inbox (and spam folder) for a message from FormSubmit and click the activation link. Until then, we cannot receive your inquiries via this form.");
          }
          throw new Error(data.message || "Email service returned an error.");
        }
      } catch (fetchErr: any) {
        console.warn("Email service failed:", fetchErr);
        if (fetchErr.message && fetchErr.message.includes("ACTION REQUIRED")) {
          throw fetchErr;
        }
      }

      if (!dbSaved && !emailSent) {
         throw new Error("Unable to submit inquiry. Please check your connection and try again.");
      }

      // 3. Show success state
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        category: INQUIRY_CATEGORIES[0],
        subject: '',
        message: ''
      });
      setLoading(false);

    } catch (err: any) {
      console.error("Form submission error:", err);
      // Don't show technical NetworkErrors; show user-friendly message
      setError(err.message || "Failed to process inquiry. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section id="inquiries" className="py-16 bg-avo-cream relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-64 bg-avo-forest/5 -skew-y-3 transform origin-top-left -z-10"></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-sm font-bold tracking-widest text-avo-gold uppercase mb-3">Get in Touch</h2>
            <h3 className="text-4xl md:text-5xl font-heading font-bold text-avo-forest mb-6">Send an Inquiry</h3>
            <p className="text-lg text-gray-600">
              Whether you're looking for wholesale partnerships, technical specifications, or general information, our team is ready to assist you.
            </p>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-3 gap-12 items-start mb-16">
          {/* Contact Information Cards */}
          <div className="lg:col-span-1 space-y-6">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100 flex flex-col items-center text-center hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 bg-avo-forest/10 text-avo-forest rounded-full flex items-center justify-center mb-4">
                <MapPin size={24} />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Visit Us</h4>
              <p className="text-gray-600">P.O Box 3169-10100<br/>Muruguru, Nyeri, Kenya</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100 flex flex-col items-center text-center hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 bg-avo-forest/10 text-avo-forest rounded-full flex items-center justify-center mb-4">
                <Phone size={24} />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Call Us</h4>
              <p className="text-gray-600 mb-4">0105662814</p>
              <a 
                href="https://wa.me/254105662814" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 rounded-full font-semibold hover:bg-[#128C7E] transition-colors shadow-sm"
              >
                <MessageCircle size={18} />
                Chat on WhatsApp
              </a>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100 flex flex-col items-center text-center hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 bg-avo-forest/10 text-avo-forest rounded-full flex items-center justify-center mb-4">
                <Mail size={24} />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Email Us</h4>
              <p className="text-gray-600 break-all">info@villaavoils.com</p>
            </motion.div>
          </div>

          {/* Form Area */}
          <div className="lg:col-span-2 bg-white rounded-3xl shadow-xl shadow-stone-200/50 p-8 md:p-12 border border-stone-100">
            <AnimatePresence mode="wait">
              {success ? (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="text-center py-16"
                >
                  <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={40} strokeWidth={2} />
                  </div>
                  <h3 className="text-3xl font-heading font-bold text-gray-900 mb-4">Message Sent Successfully</h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto">
                    Thank you for reaching out. Our team will review your inquiry and get back to you shortly.
                  </p>
                  <button 
                    onClick={() => setSuccess(false)}
                    className="px-8 py-3 bg-black text-white rounded-full font-bold hover:bg-gray-800 transition-colors shadow-md"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Full Name</label>
                      <input 
                        required
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Jane Doe"
                        className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black/30 focus:border-black outline-none transition-all text-gray-900 placeholder:text-gray-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Email Address</label>
                      <input 
                        required
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="jane@example.com"
                        className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black/30 focus:border-black outline-none transition-all text-gray-900 placeholder:text-gray-400"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Phone Number</label>
                      <input 
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+254 700 000 000"
                        className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black/30 focus:border-black outline-none transition-all text-gray-900 placeholder:text-gray-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Location</label>
                      <input 
                        required
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="City, Country"
                        className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black/30 focus:border-black outline-none transition-all text-gray-900 placeholder:text-gray-400"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Category</label>
                      <select 
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black/30 focus:border-black outline-none transition-all text-gray-900 appearance-none"
                      >
                        {INQUIRY_CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Subject</label>
                      <input 
                        required
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="What is this regarding?"
                        className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black/30 focus:border-black outline-none transition-all text-gray-900 placeholder:text-gray-400"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Message</label>
                    <textarea 
                      required
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Tell us more about your inquiry..."
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black/30 focus:border-black outline-none transition-all resize-none text-gray-900 placeholder:text-gray-400"
                    />
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
                      <AlertCircle size={16} />
                      {error}
                    </div>
                  )}

                  <button 
                    disabled={loading}
                    type="submit"
                    className="w-full bg-black text-white py-4 rounded-xl font-bold text-lg hover:bg-gray-800 transition-all flex items-center justify-center gap-3 shadow-lg shadow-black/20 active:scale-[0.98] disabled:opacity-70 mt-6"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={24} />
                    ) : (
                      <>
                        <span>Send Inquiry</span>
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Map Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="w-full h-72 bg-stone-200 rounded-3xl overflow-hidden shadow-md border border-stone-100"
        >
          <iframe 
            src="https://maps.google.com/maps?q=-0.451933,37.031717&hl=en&z=15&output=embed" 
            width="100%" 
            height="100%" 
            style={{ border: 0 }} 
            allowFullScreen 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="Villa Avoils Location"
          ></iframe>
        </motion.div>
      </div>
    </section>
  );
};
